/**
 * Buyer Portal API Client
 *
 * All buyer-specific endpoints live under /api/marketplace/buyer/*.
 * Uses the esanza_buyer_token from localStorage for authentication.
 */

import { API_URL } from './api';

const TOKEN_KEY = 'esanza_buyer_token';

function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function authHeaders(): Record<string, string> {
  const token = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
): Promise<T> {
  const url = `${API_URL}${path}`;
  const init: RequestInit = {
    method,
    headers: authHeaders(),
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }

  const res = await fetch(url, init);
  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || data.message || `Request failed: ${res.status}`);
  }
  return data as T;
}

// ─── Buyer Orders ────────────────────────────────────────────────────────────
export async function fetchBuyerOrders(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/buyer/orders${qs}`);
}

export async function fetchBuyerOrderDetail(id: number) {
  return request<any>('GET', `/api/marketplace/buyer/orders/${id}`);
}

export async function createBuyerOrder(data: any) {
  return request<any>('POST', '/api/marketplace/buyer/orders', data);
}

export async function transitionBuyerOrder(id: number, status: string) {
  return request<any>('PATCH', `/api/marketplace/buyer/orders/${id}/status`, { status });
}

// ─── Buyer Overview ──────────────────────────────────────────────────────────
export async function fetchBuyerOverview() {
  return request<any>('GET', '/api/marketplace/buyer/overview');
}

// ─── Buyer Deliveries ────────────────────────────────────────────────────────
export async function fetchBuyerDeliveries(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/buyer/deliveries${qs}`);
}

// ─── Buyer Invoices ──────────────────────────────────────────────────────────
export async function fetchBuyerInvoices(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/buyer/invoices${qs}`);
}

export async function downloadBuyerInvoicePdf(id: number, filename: string) {
  const token = getToken();
  const url = `${API_URL}/api/marketplace/buyer/invoices/${id}/pdf`;
  const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Download failed' }));
    throw new Error(err.error || 'Download failed');
  }
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
}

// ─── Buyer Profile ───────────────────────────────────────────────────────────
export async function fetchBuyerProfile() {
  return request<any>('GET', '/api/marketplace/buyer/profile');
}

export async function updateBuyerProfile(data: any) {
  return request<any>('POST', '/api/marketplace/buyer/profile', data);
}

// ─── Buyer Organisation ─────────────────────────────────────────────────────
export async function fetchBuyerOrganisation() {
  return request<any>('GET', '/api/marketplace/b2b/buyer/organisation');
}

export async function saveBuyerOrganisation(data: any) {
  return request<any>('POST', '/api/marketplace/b2b/buyer/organisation', data);
}

export async function fetchBuyerMembers() {
  return request<any>('GET', '/api/marketplace/b2b/buyer/organisation/members');
}

// ─── Buyer Quotations ────────────────────────────────────────────────────────
export async function fetchBuyerQuotations(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/buyer/quotations${qs}`);
}

export async function fetchBuyerQuotationDetail(id: number) {
  return request<any>('GET', `/api/marketplace/buyer/quotations/${id}`);
}

export async function acceptBuyerQuotation(id: number) {
  return request<any>('POST', `/api/marketplace/buyer/quotations/${id}/accept`, {});
}

export async function rejectBuyerQuotation(id: number, reason?: string) {
  return request<any>('POST', `/api/marketplace/buyer/quotations/${id}/reject`, { reason });
}

// ─── RFQs ────────────────────────────────────────────────────────────────────
export async function fetchBuyerRfqs(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/b2b/rfqs${qs}`);
}

export async function fetchBuyerRfqDetail(id: number) {
  return request<any>('GET', `/api/marketplace/b2b/rfqs/${id}`);
}

export async function createBuyerRfq(data: any) {
  return request<any>('POST', '/api/marketplace/b2b/rfqs', data);
}

export async function publishBuyerRfq(id: number) {
  return request<any>('POST', `/api/marketplace/b2b/rfqs/${id}/publish`, {});
}

export async function fetchQuotationsForRfq(rfqId: number) {
  return request<any>('GET', `/api/marketplace/b2b/rfqs/${rfqId}/quotations`);
}

// ─── Catalogue / Listings ────────────────────────────────────────────────────
export async function browseListings(params?: Record<string, any>) {
  const qs = buildQuery(params);
  return request<any>('GET', `/api/marketplace/b2b/listings${qs}`);
}

// ─── Verified Suppliers ──────────────────────────────────────────────────────
export async function fetchVerifiedSuppliers() {
  return request<any>('GET', '/api/marketplace/b2b/supplier/verified');
}

// ─── Membership Status ───────────────────────────────────────────────────────
export async function fetchMembershipStatus() {
  return request<any>('GET', '/api/marketplace/b2b/membership-status');
}

// ─── Public Marketplace ──────────────────────────────────────────────────────
export async function fetchPublicListings(params?: Record<string, any>) {
  const qs = buildQuery(params);
  const res = await fetch(`${API_URL}/api/marketplace/public/listings${qs}`);
  if (!res.ok) throw new Error('Failed to load listings');
  return res.json();
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildQuery(params?: Record<string, any>): string {
  if (!params || Object.keys(params).length === 0) return '';
  const entries = Object.entries(params).filter(
    ([, v]) => v !== undefined && v !== null && String(v) !== ''
  );
  if (entries.length === 0) return '';
  return '?' + new URLSearchParams(entries.map(([k, v]) => [k, String(v)])).toString();
}
